/** Shared fill for unattached volumes and the unattached status key. */
const UNATTACHED_VOLUME_FILL = '#F8F9FB';

/** Managed / autoscaling ring stroke — Figma solid + dashed autoscaling border. */
const MANAGED_RING_STROKE = '#98E5C1';

/** Autoscaling status group fill (solid border variant). */
const AUTOSCALING_GROUP_FILL = '#F0FBF5';

/** Pending / potentiallySupported fill — Figma sensor bubble. */
const PENDING_STATUS_FILL = '#FFF4E8';

/** Supported status-group fill when wrapping visible nested bubbles. */
const SUPPORTED_WITH_CHILDREN_FILL = '#F5F9FD';

/** Unsupported status-group fill when wrapping visible nested bubbles. */
const UNSUPPORTED_WITH_CHILDREN_FILL = '#F8F9FB';

/** Volume / status / overflow bubble map — non-infrastructure canvas styling. */
export const volumeBubbleStyleTokens = {
  status: {
    withChildrenFill: 'var(--color-white)',
    /** Per-status fill/stroke override when the bubble wraps visible nested bubbles. */
    withChildrenByKey: {
      supported: { fill: SUPPORTED_WITH_CHILDREN_FILL, stroke: 'var(--color-primary-100)' },
      unsupported: { fill: UNSUPPORTED_WITH_CHILDREN_FILL, stroke: 'var(--color-gray-300)' },
    },
    muted: {
      fill: 'var(--activation-unsupported-fill)',
      stroke: 'var(--color-gray-300)',
    },
    keys: {
      supported: {
        fill: 'var(--activation-toBeActivated-fill)',
        stroke: 'var(--exploration-supported)',
      },
      active: {
        fill: AUTOSCALING_GROUP_FILL,
        stroke: MANAGED_RING_STROKE,
      },
      unattached: {
        fill: UNATTACHED_VOLUME_FILL,
        stroke: '#DCE5F3',
      },
      toBeActivated: {
        fill: 'var(--activation-toBeActivated-fill)',
        stroke: 'var(--activation-toBeActivated)',
      },
      potentiallySupported: {
        fill: PENDING_STATUS_FILL,
        stroke: 'var(--activation-potentiallySupported)',
      },
      unsupported: {
        fill: 'var(--activation-unsupported-fill)',
        stroke: 'var(--color-gray-300)',
      },
    },
    hover: {
      signalStrokeOpacity: 1,
      mutedBorder: 'var(--color-primary-500)',
    },
    mutedStrokeOpacity: 0.3,
  },
  volume: {
    default: {
      fill: '#FBFCFC',
      stroke: '#EBEDF2',
      fillOpacity: 1,
    },
    unattached: {
      fill: UNATTACHED_VOLUME_FILL,
    },
    managed: {
      outerFill: AUTOSCALING_GROUP_FILL,
      outerStroke: MANAGED_RING_STROKE,
      innerFill: '#E9F9F1',
      innerStroke: MANAGED_RING_STROKE,
      hoverStroke: MANAGED_RING_STROKE,
      dash: '4,3',
      emphasis: {
        outerFill: '#ECFDF5',
        innerFill: '#E3FCF2',
        innerStroke: '#A7F5D9',
      },
    },
    hover: {
      stroke: 'var(--color-primary-500)',
    },
    dim: {
      fill: '#FBFCFC',
      stroke: 'var(--color-gray-100)',
    },
  },
  overflow: {
    fill: 'var(--color-white)',
    stroke: 'var(--color-gray-300)',
    labelFill: 'var(--color-gray-800)',
    sizeFill: 'var(--color-gray-700)',
    hoverStroke: 'var(--color-primary-500)',
  },
} as const;

export type VolumeBubbleStyleTokens = typeof volumeBubbleStyleTokens;
